import React, { Suspense, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  onError,
} from "@mui/material";
import { FormLazyByModel } from "./forms";

import { useSnackbar } from "notistack";

import {
  fileUpdateByModelKey,
  fileCreateByModelKey,
} from "api/FileApiRegistry";

function AddFileDialog({
  open,
  modelKey,
  mode = "create", // "create" o "edit"
  initialValues,
  onCancel,
  onSubmit,
  onCreated,
  onUpdated,
  onError,
  modelLabels = {},
}) {
  const SpecificForm = modelKey ? FormLazyByModel[modelKey] : null;
  const { enqueueSnackbar } = useSnackbar();

  const parseDRFErrors = (data) => {
    if (!data || typeof data !== "object") return { message: null, fields: {} };

    const fields = {};
    const missing = [];

    Object.entries(data).forEach(([key, value]) => {
      const msg = Array.isArray(value) ? value[0] : value;

      fields[key] = msg;

      // detecta "required"
      if (msg?.toLowerCase?.().includes("required")) {
        missing.push(key);
      }
    });

    return {
      fields,
      missing,
    };
  };
  const createOrUpdateFile = async (data) => {
    try {
      let result;

      if (mode === "edit") {
        const updaterFn = fileUpdateByModelKey[modelKey];
        if (!updaterFn)
          throw new Error(`No update API for modelKey: ${modelKey}`);
        result = await updaterFn(data.id, data);
        enqueueSnackbar("File updated successfully", { variant: "success" });
        onUpdated?.(modelKey, result); // nuevo callback específico
      } else {
        const creatorFn = fileCreateByModelKey[modelKey];
        if (!creatorFn)
          throw new Error(`No create API for modelKey: ${modelKey}`);
        result = await creatorFn(data);
        enqueueSnackbar("File created successfully", { variant: "success" });
        onCreated?.(modelKey, result); // nuevo callback específico
      }

      onSubmit(modelKey, result);
      onCancel();
    } catch (err) {
      console.error("❌ Error in createOrUpdateFile:", err);
      const data = err.response?.data;
      const { fields, missing } = parseDRFErrors(data || {});
      let message = err.message;

      if (missing.length > 0) {
        message = `${missing.join(", ")} are required`;
      } else if (data?.detail) {
        message = data.detail;
      } else if (typeof data === "string") {
        message = data;
      } else if (Object.keys(fields).length > 0) {
        message = Object.values(fields).flat().join(" ");
      }

      enqueueSnackbar(`Error: ${message}`, {
        variant: "error",
      });
      onError?.(fields);
    }
  };

  // (Opcional) Pre-carga del formulario cuando se abre el diálogo
  useEffect(() => {
    if (!open || !modelKey) return;
    const loader = FormLazyByModel[modelKey];
    // React.lazy no expone un preload oficial, pero forzamos el import dinámico:
    // Nota: solo funciona si el import en forms/index.js es estático como arriba
    // eslint-disable-next-line no-unused-expressions
    loader && loader._result == null && loader._status == null; // no hace nada, pero evita warnings
  }, [open, modelKey]);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === "edit" ? "Edit" : "Add"} {modelLabels[modelKey] || "file"}
      </DialogTitle>

      {SpecificForm ? (
        <Suspense
          fallback={<DialogContent>Cargando formulario…</DialogContent>}
        >
          <SpecificForm
            initialValues={initialValues}
            onSubmit={createOrUpdateFile}
            onCancel={onCancel}
          />
        </Suspense>
      ) : (
        <>
          <DialogContent>Form not implemented for this model.</DialogContent>
          <DialogActions>
            <Button onClick={onCancel}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// ✅ PropTypes actualizados:
AddFileDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  modelKey: PropTypes.string,
  initialValues: PropTypes.object,
  mode: PropTypes.oneOf(["create", "edit"]),
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  onUpdated: PropTypes.func,
  modelLabels: PropTypes.object,
};

export default AddFileDialog;
